import { Card, Button } from 'antd';
import { FilterOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from './PageHeader';
import {
  resolveAdminBreadcrumbs,
  type AdminBreadcrumbItem,
} from '../utils/adminBreadcrumbs';

export type AdminPageWidth = 'fluid' | 'fluid-bleed' | 'canvas';

export interface AdminListPageProps {
  title: string;
  subtitle?: string;
  extra?: ReactNode;
  filters?: ReactNode;
  resultCount?: number;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  toolbarExtra?: ReactNode;
  widthMode?: AdminPageWidth;
  summary?: ReactNode;
  alerts?: ReactNode;
  breadcrumbs?: AdminBreadcrumbItem[];
  densityOps?: boolean;
  children: ReactNode;
}

export function AdminListPage({
  title,
  subtitle,
  extra,
  filters,
  resultCount,
  hasActiveFilters,
  onClearFilters,
  toolbarExtra,
  widthMode = 'fluid',
  summary,
  alerts,
  breadcrumbs,
  densityOps,
  children,
}: AdminListPageProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const showToolbar = filters || toolbarExtra;
  const crumbs = breadcrumbs ?? resolveAdminBreadcrumbs(location.pathname, t);
  void i18n.language;
  const useOpsDensity = densityOps ?? (widthMode === 'fluid' || widthMode === 'fluid-bleed');
  const rootClass = [
    'admin-list-page',
    `admin-page--${widthMode}`,
    useOpsDensity ? 'admin-page--density-ops' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        resultCount={resultCount}
        extra={extra}
        breadcrumbs={crumbs}
      />

      {summary ? <div className="admin-page-summary">{summary}</div> : null}
      {alerts ? <div className="admin-page-alerts">{alerts}</div> : null}

      <Card className="admin-table-card" bordered={false}>
        {showToolbar ? (
          <div className="admin-filter-bar">
            <div className="admin-filter-bar__filters">
              {filters ? (
                <>
                  <FilterOutlined className="admin-filter-bar__icon" aria-hidden />
                  <div className="admin-filter-bar__controls">{filters}</div>
                </>
              ) : null}
              {hasActiveFilters && onClearFilters ? (
                <Button
                  type="link"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={onClearFilters}
                  className="admin-filter-bar__clear"
                >
                  {t('common.clearFilters')}
                </Button>
              ) : null}
            </div>
            {toolbarExtra ? <div className="admin-filter-bar__actions">{toolbarExtra}</div> : null}
          </div>
        ) : null}

        <div className={`admin-table-wrap${showToolbar ? '' : ' admin-table-wrap--plain'}`}>
          {children}
        </div>
      </Card>
    </div>
  );
}

export default AdminListPage;
